import { Button, Input, Label } from '@lensora/ui'
import { useMutation } from '@tanstack/react-query'
import * as React from 'react'

import { api } from '@/lib/api'

const COOLDOWN_SECONDS = 60

interface ResendConfirmationButtonProps {
  // Known accounts (signed in, or mid-signup with the address already typed
  // once) skip the input entirely. The confirm-email page's expired-link
  // state has no email at all — the validate response carries nothing on
  // that path — so it renders the input and lets the user type it.
  email?: string | undefined
  label?: string
  variant?: 'default' | 'outline' | 'secondary'
  className?: string
}

// The resend endpoint always responds `{ sent: true }` regardless of what
// happened server-side (anti-enumeration) — there is no error branch to
// handle here. The cooldown below is a client-side UX nicety only; the real
// throttling lives server-side.
export function ResendConfirmationButton({
  email: knownEmail,
  label = 'Send me a new link',
  variant = 'outline',
  className,
}: ResendConfirmationButtonProps) {
  const [typedEmail, setTypedEmail] = React.useState('')
  const [cooldown, setCooldown] = React.useState(0)
  const [sent, setSent] = React.useState(false)

  const resend = useMutation({
    mutationFn: (address: string) =>
      api.post<{ sent: boolean }>('/api/auth/signup/resend-confirmation', { email: address }),
    onSuccess: () => {
      setSent(true)
      setCooldown(COOLDOWN_SECONDS)
    },
  })

  React.useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => clearInterval(id)
  }, [cooldown])

  const email = knownEmail ?? typedEmail

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || cooldown > 0 || resend.isPending) return
    resend.mutate(email.trim())
  }

  return (
    <form onSubmit={handleSubmit} className={className}>
      {!knownEmail && (
        <div className="mb-3 space-y-2">
          <Label htmlFor="resend-email">Email address</Label>
          <Input
            id="resend-email"
            type="email"
            required
            placeholder="you@company.com"
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
          />
        </div>
      )}
      <Button
        type="submit"
        variant={variant}
        className="w-full"
        disabled={cooldown > 0 || resend.isPending}
      >
        {cooldown > 0 ? `${label} (${cooldown}s)` : resend.isPending ? 'Sending…' : label}
      </Button>
      {sent && cooldown > 0 && (
        <p className="mt-2 text-xs text-feedback-success">
          If that address has a pending confirmation, a new link is on its way.
        </p>
      )}
    </form>
  )
}
